import { notEmpty } from '@festina-life/core'

import { useHook } from '../../../lenteHooks'
import { useSearchNavigateTemplate } from './useSearchNavigateTemplate'

/**
 * Is a custom hook that provides a unified way to search for an entity (person, company, or invoice) by an internal or external ID and, if possible, automatically navigates to the found entity.
 *
 * It sets up the search hooks for each entity type.
 *
 * When called, it runs all searches in parallel using the provided ID.
 *
 * If exactly one search succeeds (and there are no partial access errors), it automatically navigates to that entity.
 *
 * It returns an object describing which searches succeeded, if there were access issues, and whether navigation was triggered.
 */
export const useSearchNavigate = () => {
  const handleEntitySearch = useSearchNavigateTemplate()
  const personSearch = useHook('routing.participant.usePersonSearch')()
  const companySearch = useHook('routing.company.useCompanySearch')()
  const invoiceSearch = useHook('routing.invoice.useInvoiceSearch')()

  return async (internalOrExternalId: string) => {
    const {
      didAllRequestsFailDueToLackOfAccess,
      didSomeRequestsFailDueToAccess,
      successfulRequests,
    } = await handleEntitySearch({
      internalOrExternalId,
      entity: {
        companySearch,
        personSearch,
        invoiceSearch,
      },
    })

    const pureSuccessfulRequests = successfulRequests.filter(notEmpty)

    const shouldNavigate =
      pureSuccessfulRequests.length === 1 && !didSomeRequestsFailDueToAccess
    if (shouldNavigate) {
      await pureSuccessfulRequests[0].navigation()
    }

    return {
      successfulRequests: pureSuccessfulRequests,
      didSomeRequestsFailDueToAccess,
      didAllRequestsFailDueToLackOfAccess,
      navigateInProgress: shouldNavigate,
    }
  }
}
